import { isEmpty, orderBy, partition, sortBy, take, uniqBy } from 'lodash';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Avatar, Divider } from '@mui/material';

import { NEW_FEATURE_DATA_ATTRIBUTE } from 'app/constants';
import * as Models from 'contracts/models';

import useStyles from './styles';

import DoubleChevronIcon from '../../assets/double-chevron.svg';
import { formatLicenseName } from '../../utils';
import LicenseCreditBar from '../LicenseCreditBar';
import UserEmail from '../UserEmail';
import UserMenu from '../UserMenu';

interface Props {
    user: Models.UserInfo.User;
    compact?: boolean;
    className?: string;
}

const UserWidget: React.FC<Props> = ({ user, compact, className }) => {
    const { classes, cx } = useStyles();
    const { defaultLicenseId, email } = user;
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const anchor = useCallback(() => anchorRef.current!, []);
    const container = useCallback(() => containerRef.current!, []);
    const getProps = useCallback(() => ({ style: { width: containerRef.current?.offsetWidth } }), []);

    const [isOpen, setIsOpen] = useState(false);
    const openMenu = useCallback(() => setIsOpen(true), []);
    const closeMenu = useCallback(() => setIsOpen(false), []);

    const licenses = useMemo(() => {
        const active = user.licenses.filter(({ id }) => id === defaultLicenseId);
        const [free, others] = partition(user.licenses, ({ licenseType }) => /free/.test(licenseType));
        const [valid, invalid] = partition(others, ({ isValid }) => isValid);
        const latest = take(orderBy(invalid, ({ licenseExpiration }) => new Date(licenseExpiration), 'desc'));

        let output = isEmpty(valid) ? [...active, ...free, ...latest] : [...active, ...free, ...valid];

        output = uniqBy(output, ({ id }) => id);
        output = sortBy(output, ({ id }) => user.licenses.findIndex((item) => id === item.id));

        return output;
    }, [defaultLicenseId, user.licenses]);

    const license = useMemo(
        () => licenses.find(({ id }) => id === defaultLicenseId) || licenses[0],
        [defaultLicenseId, licenses]
    );

    return (
        <>
            {compact ? (
                <div className={cx(classes.compact, className)} ref={containerRef}>
                    <Avatar className={classes.avatar} ref={anchorRef} onClick={openMenu}>
                        {email[0].toUpperCase()}
                    </Avatar>
                </div>
            ) : (
                <div
                    className={cx(classes.regular, className)}
                    ref={containerRef}
                    {...{ [NEW_FEATURE_DATA_ATTRIBUTE]: 'auth-widget' }}
                >
                    <UserEmail user={user} />
                    <Divider />
                    <div className={classes.selector} ref={anchorRef} onClick={openMenu}>
                        <div className={classes.line}>
                            <DoubleChevronIcon />
                            <span className={classes.licenseName}>{formatLicenseName(license)}</span>
                        </div>
                        <LicenseCreditBar license={license} />
                    </div>
                </div>
            )}
            <UserMenu
                anchor={anchor}
                container={container}
                getPaperProps={getProps}
                user={user}
                license={license}
                licenseList={licenses}
                isOpen={isOpen}
                onClose={closeMenu}
            />
        </>
    );
};

export default UserWidget;
